import { onCleanup } from "solid-js";

const FOCUSABLE = "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea, summary, [tabindex='0']";

/** What a dialog does with the keyboard, for its ref: the focus goes in on opening, stays in while it's open, and goes back on closing; Esc closes it where that
 *  is safe. Every dialog left it on the page behind, Next hand 7 to 9 Tabs away after every hand, and Esc reached the game (UX review 2026-11-02). The primary
 *  control is the first with data-first, else the first there is. */
export function holdFocus(el: HTMLElement, onEscape?: () => void): void {
  const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const within = () => [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => x.offsetParent !== null);
  const id = setTimeout(() => {
    if (el.contains(document.activeElement)) return;
    (el.querySelector<HTMLElement>("[data-first]") ?? within()[0] ?? el).focus({ preventScroll: true });
  }, 0);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && onEscape) {
      e.preventDefault();
      e.stopPropagation();
      onEscape();
    } else if (e.key === "Tab") {
      const all = within();
      if (all.length === 0) return e.preventDefault();
      const first = all[0]!;
      const last = all[all.length - 1]!;
      if (!el.contains(document.activeElement) || (e.shiftKey && document.activeElement === first)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  addEventListener("keydown", onKey, true);
  onCleanup(() => {
    clearTimeout(id);
    removeEventListener("keydown", onKey, true);
    if (before?.isConnected) before.focus({ preventScroll: true });
  });
}
